import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Loader2, Wand2, Download, ImageIcon } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Peacock-blue Banarasi silk with gold peacock motifs and a wide zari border",
  "Blush-pink Kanjeevaram with a contrast emerald temple border",
  "Ivory Chikankari saree with silver shadow-work florals",
  "Midnight-black georgette with scattered gold sequin stars",
  "Sunset-orange Tussar silk with tribal block prints",
];

export default function Designer() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [design, setDesign] = useState(null); // { design_id, image, prompt }
  const [mine, setMine] = useState([]);
  const resultRef = useRef(null);

  const loadMine = () => { if (user) api.get("/design/mine").then((r) => setMine(r.data)).catch(() => {}); };
  useEffect(() => { loadMine(); }, [user]);

  const generate = async (p) => {
    const text = (p ?? prompt).trim();
    if (!text) { toast.error("Describe the saree you'd like"); return; }
    if (p) setPrompt(p);
    setLoading(true);
    setDesign(null);
    try {
      const res = await api.post("/design", { prompt: text });
      setDesign(res.data);
      toast.success("Your design is ready!");
      loadMine();
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Design generation failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const tryOn = (d) => {
    sessionStorage.setItem("tashora_pending_design", JSON.stringify({ image: d.image, name: d.prompt?.slice(0, 40) || "My AI Design" }));
    navigate(`/try-on?design=${d.design_id}`);
  };

  const download = (d) => {
    const a = document.createElement("a");
    a.href = d.image; a.download = "tashora-design.png"; a.click();
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-12" data-testid="designer-page">
      <div className="text-center mb-10">
        <span className="caption">AI Saree Designer</span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-gold-light mt-3">
          Dream it, <span className="gold-text italic">design it</span>
        </h1>
        <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
          Describe your perfect saree and our AI will weave it into a one-of-a-kind design — then drape it on you.
        </p>
      </div>

      <div className="grid lg:grid-cols-[1fr_1.1fr] gap-8">
        {/* Prompt */}
        <div>
          <p className="caption mb-3">Describe your saree</p>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            data-testid="design-prompt-input"
            rows={4}
            placeholder="e.g. Royal maroon Banarasi silk with gold lotus motifs and a broad zari border"
            className="w-full bg-obsidian-2 border border-gold/25 rounded-lg p-4 text-sm text-gold-muted focus:outline-none focus:border-gold resize-none"
          />
          <div className="flex flex-wrap gap-2 mt-4">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => generate(s)} data-testid="design-suggestion"
                className="text-xs text-left border border-gold/25 text-gold-muted rounded-full px-3 py-1.5 hover:border-gold hover:bg-gold/5 transition-colors">
                {s.length > 42 ? s.slice(0, 42) + "…" : s}
              </button>
            ))}
          </div>
          <button
            onClick={() => generate()}
            disabled={loading}
            data-testid="generate-design-button"
            className="mt-6 w-full bg-gold text-obsidian font-semibold py-3.5 rounded-full hover:bg-gold-light transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            {loading ? "Designing… ~15s" : "Generate my design"}
          </button>
          {!user && <p className="text-xs text-muted-foreground text-center mt-3">Sign in to save your designs to your account.</p>}
        </div>

        {/* Result */}
        <div ref={resultRef}>
          <p className="caption mb-3">Your design</p>
          <div className="relative rounded-lg overflow-hidden gold-border bg-obsidian-2 aspect-[4/5]" data-testid="design-result">
            <AnimatePresence mode="wait">
              {loading ? (
                <motion.div key="l" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 grid place-items-center">
                  <div className="text-center">
                    <div className="w-12 h-12 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto" />
                    <p className="caption mt-5">Weaving your design</p>
                  </div>
                </motion.div>
              ) : design ? (
                <motion.img key="r" src={design.image} alt="Your saree design" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="absolute inset-0 w-full h-full object-cover" data-testid="design-result-image" />
              ) : (
                <motion.div key="e" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute inset-0 grid place-items-center text-center px-8">
                  <div><ImageIcon className="w-10 h-10 text-gold/40 mx-auto" /><p className="text-sm text-muted-foreground mt-4">Your unique saree design will appear here.</p></div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {design && (
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button onClick={() => download(design)} data-testid="design-download-button" className="border border-gold/40 text-gold-muted py-3 rounded-full hover:bg-gold/10 transition-colors flex items-center justify-center gap-2">
                <Download className="w-4 h-4" /> Download
              </button>
              <button onClick={() => tryOn(design)} data-testid="design-tryon-button" className="bg-gold text-obsidian font-semibold py-3 rounded-full hover:bg-gold-light transition-colors flex items-center justify-center gap-2">
                <Sparkles className="w-4 h-4" /> Try this on me
              </button>
            </div>
          )}
        </div>
      </div>

      {/* My designs */}
      {user && mine.length > 0 && (
        <div className="mt-20">
          <div className="hairline mb-8" />
          <h2 className="font-serif text-3xl text-gold-light mb-6">My designs</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="my-designs">
            {mine.map((d) => (
              <div key={d.design_id} className="rounded-lg overflow-hidden gold-border bg-obsidian-2" data-testid={`my-design-${d.design_id}`}>
                <img src={d.image} alt={d.prompt} className="w-full aspect-[4/5] object-cover" />
                <p className="text-[11px] text-muted-foreground px-2 pt-2 line-clamp-2">{d.prompt}</p>
                <button onClick={() => tryOn(d)} data-testid={`my-design-tryon-${d.design_id}`} className="m-2 w-[calc(100%-1rem)] text-[11px] bg-gold text-obsidian font-semibold py-1.5 rounded hover:bg-gold-light transition-colors flex items-center justify-center gap-1">
                  <Sparkles className="w-3 h-3" /> Try on
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
